import React, { useState } from 'react';
import { Layers, ChevronLeft, ChevronRight } from 'lucide-react';
import { Modal } from '@/Components/Common/Tailwind';
import { SectionHeader } from './SectionHeader';

interface CarouselGalleryCardProps {
    carousel: any; // Using any for now as carousel structure varies
    onClick: () => void;
}

function CarouselGalleryCard({ carousel, onClick }: CarouselGalleryCardProps) {
    const images = carousel?.images || [];
    const firstImage = images[0];
    const remainingCount = images.length - 1;
    const src = firstImage?.src || firstImage?.image || firstImage?.url;

    if (!src) return null;

    return (
        <div
            onClick={onClick}
            className="group relative bg-white rounded-2xl overflow-hidden shadow-[0_1px_2px_rgba(0,0,0,0.04),0_6px_16px_-4px_rgba(0,0,0,0.08)] hover:shadow-[0_2px_4px_rgba(0,0,0,0.05),0_12px_28px_-6px_rgba(0,0,0,0.12)] transition-[box-shadow,transform] duration-200 active:scale-[0.96] cursor-pointer"
        >
            <div className="aspect-square relative">
                <img
                    src={src}
                    alt={carousel.name || 'Gallery'}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110 outline outline-1 -outline-offset-1 outline-black/10"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-60" />

                {/* Indicators */}
                <div className="absolute bottom-2.5 left-2.5 flex items-center gap-1.5">
                    <div className="w-5 h-5 bg-white/20 backdrop-blur-md rounded-lg flex items-center justify-center text-white border border-white/30">
                        <Layers size={10} />
                    </div>
                    <span className="text-[10px] font-bold text-white shadow-sm">
                        {images.length}
                    </span>
                </div>

                {remainingCount > 0 && (
                    <div className="absolute top-2.5 right-2.5 bg-black/40 backdrop-blur-md text-white px-2 py-0.5 rounded-full text-[9px] font-black border border-white/20">
                        +{remainingCount}
                    </div>
                )}
            </div>

            {carousel.name && (
                <div className="p-2.5">
                    <h3 className="text-base font-bold text-gray-700 truncate">{carousel.name}</h3>
                </div>
            )}
        </div>
    );
}

interface CarouselGalleryFeedProps {
    carousels: any[];
    isLoading: boolean;
}

export default function CarouselGalleryFeed({ carousels, isLoading }: CarouselGalleryFeedProps) {
    const [selectedCarousel, setSelectedCarousel] = useState<any>(null);
    const [currentSlide, setCurrentSlide] = useState(0);

    const galleryCarousels = (carousels || []).slice(1);

    if (!isLoading && !galleryCarousels.length) return null;

    const handleOpen = (carousel: any) => {
        setSelectedCarousel(carousel);
        setCurrentSlide(0);
    };

    const handleNext = (e: React.MouseEvent, total: number) => {
        e.stopPropagation();
        setCurrentSlide((prev) => (prev + 1) % total);
    };

    const handlePrev = (e: React.MouseEvent, total: number) => {
        e.stopPropagation();
        setCurrentSlide((prev) => (prev - 1 + total) % total);
    };

    const selectedImages = selectedCarousel?.images || [];

    // Helper to get image URL
    const getImageUrl = (img: any) => img?.src || img?.image || img?.url || img;

    return (
        <section className="p-6 pb-8">
            <SectionHeader title="갤러리" />

            <div className="grid grid-cols-2 gap-4">
                {galleryCarousels.map((carousel: any, idx: number) => (
                    <CarouselGalleryCard
                        key={carousel.id || idx}
                        carousel={carousel}
                        onClick={() => handleOpen(carousel)}
                    />
                ))}
            </div>

            <Modal isOpen={!!selectedCarousel} toggle={() => setSelectedCarousel(null)} variant="full" className="gap-0 border-0 bg-white">
                    {selectedCarousel && (
                        <div className="flex min-h-full flex-col">
                            <div className="flex items-center gap-2 border-b border-gray-100 px-4 py-3">
                                <button
                                    type="button"
                                    className="flex items-center justify-center w-8 h-8 rounded-full text-gray-700 hover:bg-gray-100"
                                    aria-label="Back"
                                    onClick={() => setSelectedCarousel(null)}
                                >
                                    <ChevronLeft size={18} />
                                </button>
                                <h3 className="text-base font-semibold text-gray-900">
                                    {selectedCarousel.name || "갤러리"}
                                </h3>
                            </div>

                            {/* Image Area */}
                            <div className="relative flex-1 bg-white flex items-center justify-center">
                                <div className="absolute inset-0 flex items-center justify-center">
                                    {selectedImages.map((img: any, idx: number) => (
                                        <div
                                            key={idx}
                                            className={`absolute inset-0 transition-opacity duration-300 flex items-center justify-center ${idx === currentSlide ? 'opacity-100 z-10' : 'opacity-0 z-0'
                                                }`}
                                        >
                                            <img
                                                src={getImageUrl(img)}
                                                alt={`Slide ${idx + 1}`}
                                                className="w-full h-full object-cover"
                                            />
                                        </div>
                                    ))}
                                </div>

                                {selectedImages.length > 1 && (
                                    <>
                                        <button
                                            onClick={(e) => handlePrev(e, selectedImages.length)}
                                            className="absolute left-2 top-1/2 -translate-y-1/2 z-20 w-10 h-10 flex items-center justify-center rounded-full bg-black/40 backdrop-blur-md border border-white/20 text-white hover:bg-black/60 transition-colors active:scale-[0.96]"
                                        >
                                            <ChevronLeft size={22} />
                                        </button>
                                        <button
                                            onClick={(e) => handleNext(e, selectedImages.length)}
                                            className="absolute right-2 top-1/2 -translate-y-1/2 z-20 w-10 h-10 flex items-center justify-center rounded-full bg-black/40 backdrop-blur-md border border-white/20 text-white hover:bg-black/60 transition-colors active:scale-[0.96]"
                                        >
                                            <ChevronRight size={22} />
                                        </button>

                                        {/* Dots */}
                                        <div className="absolute bottom-4 left-0 right-0 z-20 flex justify-center gap-1.5">
                                            {selectedImages.map((_: any, idx: number) => (
                                                <div
                                                    key={idx}
                                                    className={`h-1.5 rounded-full transition-[width,background-color] duration-300 ${idx === currentSlide ? 'w-4 bg-white' : 'w-1.5 bg-white/40'
                                                        }`}
                                                />
                                            ))}
                                        </div>
                                    </>
                                )}
                            </div>

                            {/* Info Area */}
                            <div className="bg-white p-6 w-full flex flex-col">
                                <p className="text-sm text-gray-700 mb-4 whitespace-pre-wrap">
                                    {selectedCarousel.description || "등록된 설명이 없습니다."}
                                </p>

                                <div className="mt-auto pt-4 border-t border-gray-100 flex justify-between items-center text-xs text-gray-500 font-medium font-mono">
                                    <span>{currentSlide + 1} / {selectedImages.length}</span>
                                </div>
                            </div>
                        </div>
                    )}
            </Modal>
        </section>
    );
}
